import { useLazyQuery, useQuery } from '@apollo/client'

import { CLICK_CODE, extractByTypename, isNotNullish } from '@local/utils'

import { useErrorToast } from '@web/hooks/useErrorToast'
import { federationApolloClient } from '@web/libs/apollo/federationClient'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import { convertUpcomingOrderItem } from '@web/pages/v5/home/UpcomingSection/helper'
import {
  UpcomingOrderQuery,
  UpcomingOrderRefreshQuery,
} from '@web/pages/v5/home/UpcomingSection/hooks/useUpcomingOrder.query'

interface Props {
  limit?: number
  skip?: boolean
}

export const useUpcomingOrder = ({ limit, skip }: Props = { limit: 10, skip: false }) => {
  const { data, loading, fetchMore, error } = useQuery(UpcomingOrderQuery, { variables: { limit }, skip })
  useErrorToast(error)
  const [refreshUpcomingOrderQuery, { loading: refreshLoading }] = useLazyQuery(UpcomingOrderRefreshQuery, {
    fetchPolicy: 'network-only',
  })
  const { MeSucceed } = extractByTypename(data?.me)
  const upcomingOrders = MeSucceed?.upcomingOrders

  const upcomingOrderRefresh = (id: string) => {
    sendClickLog({ area: CLICK_CODE['timelinepg.upcomingrefresh'] })
    refreshUpcomingOrderQuery({ variables: { id } })
  }

  const upcomingOrderCount = upcomingOrders?.pageInfo.totalCount ?? 0
  const upcomingOrderItems =
    upcomingOrders?.orders?.filter(isNotNullish).map(item => ({
      ...convertUpcomingOrderItem(item),
      isLoading: refreshLoading,
      onClickRefresh: () => upcomingOrderRefresh(item.id),
    })) ?? []

  const nextPage = upcomingOrders?.pageInfo.nextPage
  const hasNextPage = !!upcomingOrders?.pageInfo.hasNextPage

  const upcomingOrderFetchMore = async () => {
    if (nextPage && hasNextPage && !skip) {
      return await fetchMore({
        variables: { limit, page: nextPage },
      })
    }
    return null
  }

  const resetUpcomingOrder = () => {
    federationApolloClient?.cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        me(existing) {
          return {
            ...existing,
            upcomingOrders: undefined,
          }
        },
      },
    })
  }

  return {
    upcomingOrderItems,
    upcomingOrderCount,
    upcomingOrderFetchMore,
    upcomingOrderMore: hasNextPage,
    upcomingOrderRefresh,
    data,
    loading,
    resetUpcomingOrder,
  }
}
